import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/privacy.ts'
import { Footer } from './components/footer.tsx'
import Header from './components/header.tsx'

export const meta: Route.MetaFunction = () =>
	buildMeta({
		title: 'Privacy Policy - CompetitorBacklinks.com',
		description:
			'Learn how CompetitorBacklinks.com collects, uses, and protects your data. We never sell your data and use Stripe for secure payments.',
		path: '/privacy',
	})

export default function PrivacyPage() {
	return (
		<>
			<div className="relative py-6">
				<Header />
				<main className="relative container mx-auto max-w-3xl pt-16 pb-12 lg:pt-32 lg:pb-24">
					{/* Header */}

					<h1 className="mb-8 text-4xl font-bold tracking-tight sm:text-5xl">
						Privacy Policy
					</h1>

					<div className="mx-auto mt-10 space-y-8">
						<section aria-labelledby="quick-summary">
							<h2 id="quick-summary" className="text-2xl font-semibold">
								Quick Summary
							</h2>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									We only collect the information we need to provide our
									service.
								</li>
								<li>We never sell your data.</li>
								<li>
									Payments are processed securely by Stripe. We never see or
									store your card details.
								</li>
								<li>
									You can request, export, or delete your data at any time.
								</li>
								<li>
									We comply with GDPR (Europe), CCPA (California), and other
									applicable privacy laws.
								</li>
							</ul>
						</section>

						<section aria-labelledby="info-we-collect">
							<h2 id="info-we-collect" className="text-2xl font-semibold">
								1. Information We Collect
							</h2>

							<h3 className="mt-4 text-xl font-medium">
								Information You Provide
							</h3>
							<ul className="list-disc space-y-2 pl-6">
								<li>Account details (name, email address)</li>
								<li>Payment details (processed securely by Stripe)</li>
								<li>Communication preferences</li>
							</ul>

							<h3 className="mt-6 text-xl font-medium">
								Automatically Collected Information
							</h3>
							<ul className="list-disc space-y-2 pl-6">
								<li>Usage data and analytics</li>
								<li>Device and browser information</li>
								<li>IP address and location data</li>
								<li>Cookies and similar technologies</li>
							</ul>
						</section>

						<section aria-labelledby="how-we-use">
							<h2 id="how-we-use" className="text-2xl font-semibold">
								2. How We Use Your Information
							</h2>
							<p className="mt-4">We use the information we collect to:</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>Provide, maintain, and improve our services</li>
								<li>Process payments and manage subscriptions/credits</li>
								<li>Send important service updates and notifications</li>
								<li>Respond to questions and support requests</li>
								<li>Ensure security and prevent fraud</li>
								<li>Comply with legal obligations</li>
							</ul>
						</section>

						<section aria-labelledby="data-sharing">
							<h2 id="data-sharing" className="text-2xl font-semibold">
								3. Data Sharing and Disclosure
							</h2>
							<p className="mt-4">
								We do <strong>not</strong> sell, trade, or rent your personal
								information. We may share data only in these circumstances:
							</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									<strong>Service Providers:</strong> Trusted providers such as
									Stripe (payments) and Cloudflare (bot protection)
								</li>
								<li>
									<strong>Legal Requirements:</strong> When required by law or
									to protect rights and safety
								</li>
								<li>
									<strong>Business Transfers:</strong> As part of a merger,
									acquisition, or sale of assets
								</li>
								<li>
									<strong>With Your Consent:</strong> If you explicitly
									authorize us
								</li>
							</ul>
						</section>

						<section aria-labelledby="google-oauth">
							<h2 id="google-oauth" className="text-2xl font-semibold">
								4. Analytics Data
							</h2>

							<h3 className="mt-6 text-xl font-medium">
								Analytics Limited Use
							</h3>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									We comply with PostHog user data and developer policies.
								</li>
								<li>
									We do not use, transfer, or sell PostHog data to train machine
									learning or AI models.
								</li>
								<li>
									Aggregated/anonymized data is used only to provide analytics
									to you.
								</li>
							</ul>
						</section>

						<section aria-labelledby="payments">
							<h2 id="payments" className="text-2xl font-semibold">
								5. Payment Processing (Stripe)
							</h2>
							<ul className="mt-4 list-disc space-y-2 pl-6">
								<li>
									Payments are handled by Stripe, a PCI DSS compliant processor.
								</li>
								<li>Stripe processes your payment details directly.</li>
								<li>We do not store credit card numbers.</li>
								<li>
									We only receive confirmation of payment and basic billing
									info.
								</li>
								<li>
									Stripe’s{' '}
									<a
										href="https://stripe.com/privacy"
										target="_blank"
										rel="noopener noreferrer"
										className="underline"
									>
										Privacy Policy
									</a>{' '}
									governs their handling of your data.
								</li>
							</ul>
						</section>

						<section aria-labelledby="bot-protection">
							<h2 id="bot-protection" className="text-2xl font-semibold">
								6. Bot Protection (Cloudflare Turnstile)
							</h2>
							<p className="mt-4">
								Our free tools and sign-up forms are protected by Cloudflare
								Turnstile, which tells real visitors apart from automated abuse.
								Most of the time it verifies you in the background and you will
								never see it. Occasionally it asks you to confirm with a single
								click.
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6">
								<li>
									To run the check, Cloudflare receives your IP address, TLS
									fingerprint, user-agent header, and the site key and origin of
									the page you are on.
								</li>
								<li>
									Cloudflare states these signals are strictly necessary for
									detecting and blocking bots. They are not used for advertising
									or to build a profile of you.
								</li>
								<li>
									Cloudflare&rsquo;s handling of this data is governed by their{' '}
									<a
										href="https://www.cloudflare.com/turnstile-privacy-policy/"
										target="_blank"
										rel="noopener noreferrer"
										className="underline"
									>
										Turnstile Privacy Addendum
									</a>{' '}
									and their{' '}
									<a
										href="https://www.cloudflare.com/privacypolicy/"
										target="_blank"
										rel="noopener noreferrer"
										className="underline"
									>
										Privacy Policy
									</a>
									.
								</li>
							</ul>
						</section>

						<section aria-labelledby="security">
							<h2 id="security" className="text-2xl font-semibold">
								7. Data Security
							</h2>
							<p className="mt-4">
								We use industry-standard practices to protect your data:
							</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>End-to-end encryption (TLS/SSL)</li>
								<li>Encryption at rest for stored data</li>
								<li>Regular security audits and patching</li>
								<li>Access controls and multi-factor authentication</li>
								<li>Secure development practices</li>
							</ul>
						</section>

						<section aria-labelledby="retention">
							<h2 id="retention" className="text-2xl font-semibold">
								8. Data Retention
							</h2>
							<p className="mt-4">We keep data only as long as necessary:</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									Account data: Until you delete your account or request
									deletion
								</li>
								<li>
									Payment/billing records: As required for accounting and legal
									purposes
								</li>
								<li>
									Logs/analytics: Up to 12 months for service improvement and
									security
								</li>
							</ul>
						</section>

						<section aria-labelledby="rights">
							<h2 id="rights" className="text-2xl font-semibold">
								9. Your Rights and Choices
							</h2>
							<p className="mt-4">You have the right to:</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									<strong>Access</strong>: Request a copy of your data
								</li>
								<li>
									<strong>Correction</strong>: Update inaccurate info
								</li>
								<li>
									<strong>Deletion</strong>: Request account/data deletion
								</li>
								<li>
									<strong>Portability</strong>: Transfer data to another service
								</li>
								<li>
									<strong>Opt-Out</strong>: Unsubscribe from marketing
									communications
								</li>
								<li>
									<strong>Revoke Access</strong>: Disconnect Google accounts
									anytime
								</li>
							</ul>
							<p className="mt-4">
								<strong>For EU/UK Residents (GDPR):</strong> You may have
								additional rights, including the right to restrict processing
								and to lodge a complaint with your data protection authority.
							</p>
							<p className="mt-2">
								<strong>For California Residents (CCPA):</strong> You have the
								right to know, delete, and opt-out of certain data uses.
							</p>
						</section>

						<section aria-labelledby="cookies">
							<h2 id="cookies" className="text-2xl font-semibold">
								10. Cookies and Tracking
							</h2>
							<p className="mt-4">
								We use cookies and similar technologies to:
							</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>Maintain session and login status</li>
								<li>Remember user preferences</li>
								<li>Analyze service usage and performance</li>
								<li>Provide personalized features</li>
							</ul>
							<p className="mt-4">
								<strong>EU/UK users:</strong> A cookie consent banner allows you
								to manage preferences in compliance with GDPR.
							</p>
						</section>

						<section aria-labelledby="children">
							<h2 id="children" className="text-2xl font-semibold">
								11. Children’s Privacy
							</h2>
							<p className="mt-4">
								Our services are not directed to children under 13. We do not
								knowingly collect personal data from children. If we become
								aware of such data, we will delete it immediately.
							</p>
						</section>

						<section aria-labelledby="changes">
							<h2 id="changes" className="text-2xl font-semibold">
								12. Changes to This Policy
							</h2>
							<p className="mt-4">
								We may update this policy from time to time. If we make material
								changes, we will:
							</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>Email you at your registered address, or</li>
								<li>Post a notice on our website</li>
							</ul>
							<p className="mt-4">
								Your continued use of our service after changes take effect
								constitutes acceptance of the updated policy.
							</p>
						</section>

						<section aria-labelledby="contact">
							<h2 id="contact" className="text-2xl font-semibold">
								13. Contact Us
							</h2>
							<p className="mt-4">
								For any questions, concerns, or data subject requests:
							</p>
							<p className="mt-2">
								Email:{' '}
								<a
									href="mailto:info@competitorbacklinks.com"
									className="underline"
								>
									info@competitorbacklinks.com
								</a>
							</p>
						</section>
					</div>
				</main>
			</div>
			<Footer />
		</>
	)
}
